import { Assignment } from '../../models/assignments/Assignments'
import { ConsentStatus, ConsentTracking } from '../../models/consent/Consent'
import { careConsents } from '../assignments/constants'
import { AssignmentStatuses } from '../assignments/types'

const toTimestamp = (date: string | null | undefined) => (date ? new Date(date).getTime() : 0)

// Same rule as getEffectiveRecipientLyraId in assignments/utils (a consent with no recipient_id
// belongs to the episode's patient). Kept local so this module doesn't import assignments/utils,
// which imports this one.
const getRecipientLyraId = (assignment: Assignment): string | undefined =>
  assignment.recipient_id || assignment.patient_id

export const isCareConsent = (assignment?: Assignment | null): boolean =>
  !!assignment && careConsents.includes(assignment.content.name)

/**
 * A "renewal" care consent is a care consent assigned after an earlier, non-missed consent for the
 * same recipient in the same episode. LyraTherapy creates these when a signed consent is expiring,
 * has expired, or was superseded by a new version (annual refresh / version rollout).
 *
 * Only the first consent is part of the Care Onboarding flow (consent → intake → initial
 * assessment). Renewals stand alone: they are shown as their own activity and are not linked to,
 * or hidden behind, the onboarding forms.
 *
 * Scoped per recipient because an episode can have several participants (e.g. a DBT Teens teen and
 * caregiver) who each have their own consent chain. Missed consents never make a later consent a
 * renewal: if the onboarding consent was never signed and LT or a provider re-assigns it, the new
 * copy is still the onboarding consent. The missed original itself isn't a renewal either, since
 * nothing was assigned before it.
 */
export const isRenewalCareConsent = ({
  assignment,
  assignments,
}: {
  assignment?: Assignment | null
  assignments?: Assignment[] | null
}): boolean => {
  if (!assignment || !assignments?.length || !isCareConsent(assignment)) {
    return false
  }

  const recipientLyraId = getRecipientLyraId(assignment)
  const createDate = new Date(assignment.create_date)
  return assignments.some(
    (assignmentsItem) =>
      assignmentsItem.id !== assignment.id &&
      isCareConsent(assignmentsItem) &&
      assignmentsItem.status !== AssignmentStatuses.missed &&
      assignmentsItem.episode_id === assignment.episode_id &&
      getRecipientLyraId(assignmentsItem) === recipientLyraId &&
      new Date(assignmentsItem.create_date) < createDate,
  )
}

/**
 * Whether an unsigned care consent should be hidden from the client because LyraTherapy has
 * already moved on from it. A consent is stale when its consent_tracking record is no longer
 * PENDING (it expired / was superseded / is expiring) AND a newer PENDING consent exists for the
 * same recipient in the episode — the client should only ever be asked to sign the pending one.
 *
 * Signed (completed) consents are never stale: they stay visible as history. Consents without a
 * tracking record are left alone (tracking may have been disabled when they were assigned).
 */
export const isStaleCareConsent = ({
  assignment,
  assignments,
  consents,
}: {
  assignment?: Assignment | null
  assignments?: Assignment[] | null
  consents?: ConsentTracking[] | null
}): boolean => {
  if (!assignment || !consents?.length || !isCareConsent(assignment)) return false
  if (assignment.status === AssignmentStatuses.completed) return false

  const ownConsent = consents.find((consent) => consent.assignment_id === assignment.id)
  if (!ownConsent || ownConsent.status === ConsentStatus.PENDING) return false

  const recipientLyraId = getRecipientLyraId(assignment)
  const hasNewerPendingConsent = consents.some(
    (consent) =>
      consent.status === ConsentStatus.PENDING &&
      consent.recipient_id === recipientLyraId &&
      consent.assignment_id !== assignment.id &&
      assignments?.some(
        (candidate) => candidate.id === consent.assignment_id && candidate.episode_id === assignment.episode_id,
      ),
  )
  return hasNewerPendingConsent
}

/**
 * The consent record a recipient's pending consent replaced: their latest record that is not
 * PENDING (SIGNED / EXPIRING_SOON / EXPIRED / SUPERSEDED). Its status is what explains why the
 * recipient is being asked to sign again.
 *
 * `excludeAssignmentId` leaves out the record for the consent being evaluated so it never counts
 * as its own predecessor. Returns undefined when the recipient has no earlier record (first-ever
 * consent) or there is no consent data.
 */
export const getPreviousConsentForRecipient = ({
  consents,
  recipientLyraId,
  excludeAssignmentId,
}: {
  consents?: ConsentTracking[] | null
  recipientLyraId?: string | null
  excludeAssignmentId?: string | null
}): ConsentTracking | undefined => {
  if (!consents?.length || !recipientLyraId) return undefined

  return consents
    .filter(
      (consent) =>
        consent.recipient_id === recipientLyraId &&
        consent.status !== ConsentStatus.PENDING &&
        (!excludeAssignmentId || consent.assignment_id !== excludeAssignmentId),
    )
    .reduce<ConsentTracking | undefined>((latest, consent) => {
      if (!latest) return consent
      const consentTime = toTimestamp(consent.create_date) || toTimestamp(consent.update_date)
      const latestTime = toTimestamp(latest.create_date) || toTimestamp(latest.update_date)
      return consentTime > latestTime ? consent : latest
    }, undefined)
}

export type RenewalCareConsentReason = ConsentStatus.EXPIRING_SOON | ConsentStatus.EXPIRED | ConsentStatus.SUPERSEDED

/**
 * Why a renewal care consent was assigned, taken from the recipient's latest non-pending consent
 * record: the previous form is EXPIRING_SOON, has EXPIRED, or was SUPERSEDED by a new version.
 *
 * Returns undefined when the reason can't be determined — no consent data, no earlier record for
 * the recipient, or the earlier record is still SIGNED (e.g. a second consent assigned manually).
 */
export const getRenewalCareConsentReason = ({
  assignment,
  consents,
}: {
  assignment?: Assignment | null
  consents?: ConsentTracking[] | null
}): RenewalCareConsentReason | undefined => {
  if (!assignment || !isCareConsent(assignment)) return undefined

  const previousConsent = getPreviousConsentForRecipient({
    consents,
    recipientLyraId: getRecipientLyraId(assignment),
    excludeAssignmentId: assignment.id,
  })

  if (
    previousConsent?.status === ConsentStatus.EXPIRING_SOON ||
    previousConsent?.status === ConsentStatus.EXPIRED ||
    previousConsent?.status === ConsentStatus.SUPERSEDED
  ) {
    return previousConsent.status
  }
  return undefined
}
